"use client";

import Link from "next/link";
import clsx from "clsx";
import { useEffect, useRef, useState } from "react";
import type { NavGroup } from "./HeaderNavClient";
import { Beef, ChartNoAxesCombined, FileChartColumn, MapPin, Scale, ShoppingBasket, TrendingUp } from "lucide-react";

interface Props {
  group: NavGroup;
  isActive: (href: string) => boolean;
}

/** Desktop nav grup dropdown'u — hover veya tikla ile acilir, disari tikla/Escape kapatir. */
export function HeaderNavDropdown({ group, isActive }: Props) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);
  const groupActive = group.items.some((item) => isActive(item.href));
  const triggerRef = useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setOpen(false);
        triggerRef.current?.focus();
      }
    };
    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <div
      ref={ref}
      className="relative"
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
    >
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen(true)}
        aria-expanded={open}
        aria-haspopup="menu"
        className={clsx(
          "relative inline-flex items-center gap-1 px-3 py-5 text-[14px] font-medium transition-colors after:absolute after:inset-x-3 after:bottom-3 after:h-0.5 after:origin-center after:bg-(--color-accent) after:transition-transform",
          groupActive
            ? "text-(--color-foreground) after:scale-x-100"
            : "text-(--color-foreground) after:scale-x-0 hover:text-(--color-brand)",
        )}
      >
        {group.label}
        <svg
          width="12"
          height="12"
          viewBox="0 0 20 20"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
          className={clsx("transition-transform", open && "rotate-180")}
          aria-hidden
        >
          <path d="M5 7.5l5 5 5-5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>

      {open && (
        <div role="menu" className="absolute left-0 top-full z-50 pt-0">
          <div className="grid w-[min(860px,calc(100vw-4rem))] grid-cols-[1fr_1fr_260px] overflow-hidden rounded-b-[8px] border border-(--color-border-soft) bg-(--color-surface) shadow-(--shadow-card)">
            <MenuColumn title="Fiyatları keşfet" items={group.items.filter((item) => item.section === "discover")} isActive={isActive} onSelect={() => setOpen(false)} />
            <MenuColumn title="Karşılaştır ve incele" items={group.items.filter((item) => item.section === "compare")} isActive={isActive} onSelect={() => setOpen(false)} />
            <div className="m-4 border-l border-(--color-border-soft) bg-(--color-background) p-6">
              <h3 className="font-display text-xl font-semibold">Nereden başlamalı?</h3>
              <p className="mt-3 text-sm leading-6 text-(--color-muted)">Merak ettiğiniz bir ürünün fiyatını keşfederek piyasayı daha iyi anlayabilirsiniz.</p>
              <Link href="/hal-fiyatlari" onClick={() => setOpen(false)} className="mt-5 inline-flex items-center gap-2 text-sm font-semibold text-(--color-accent)">Tüm fiyatları aç <span aria-hidden>→</span></Link>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const ICONS = {
  prices: ShoppingBasket,
  borsa: FileChartColumn,
  "canli-hayvan": Beef,
  et: Beef,
  compare: Scale,
  index: TrendingUp,
  map: MapPin,
} as const;

function MenuColumn({ title, items, isActive, onSelect }: {
  title: string;
  items: NavGroup["items"];
  isActive: (href: string) => boolean;
  onSelect: () => void;
}) {
  return (
    <div className="border-r border-(--color-border-soft) p-6">
      <h3 className="mb-3 font-display text-xl font-semibold">{title}</h3>
      <div className="space-y-1">
        {items.map((item) => {
          const Icon = ICONS[item.key as keyof typeof ICONS] ?? ChartNoAxesCombined;
          return (
            <Link key={item.key} href={item.href} prefetch={false} role="menuitem" onClick={onSelect} className={clsx("flex gap-3 border-l-2 px-3 py-3 transition-colors", isActive(item.href) ? "border-(--color-accent) bg-(--color-brand-light)" : "border-transparent hover:border-(--color-accent) hover:bg-(--color-bg-alt)")}>
              <Icon className="mt-0.5 h-5 w-5 shrink-0" strokeWidth={1.8} aria-hidden />
              <span><strong className="block text-sm font-semibold text-(--color-foreground)">{item.label}</strong><span className="mt-0.5 block text-xs leading-5 text-(--color-muted)">{item.description}</span></span>
            </Link>
          );
        })}
      </div>
    </div>
  );
}
